import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import {
    connect,
    ConnectedProps,
    useDispatch,
} from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Bronze from '../../resources/img/loyalty/bronze.svg';
import Diamond from '../../resources/img/loyalty/diamond.svg';
import Gold from '../../resources/img/loyalty/gold.svg';
import Silver from '../../resources/img/loyalty/silver.svg';
import { getCompanyLoyaltyRules } from '../../store/actions/settings';
import { ApplicationState } from '../../store/types';
import Title from '../common/title';

interface LoyaltyTier {
    code?: string;
    startAmount: number;
    endAmount: number | null;
    percent: number;
    multiplier: number;
}

interface LoyaltyIProps {
    loyalty: {
        id: number;
        companyId: number;
        totalSpending: number;
        loyalty: string;
        points: number;
    };
    school?: {
        id: number;
        name: string;
    };
    tiers: LoyaltyTier[];
}

interface LevelInfos {
    currentImage?: string;
    nextImage?: string;
    currentStartAmount: number;
    nextStartAmount?: number;
}

type LoyaltyProps = LoyaltyIProps & WrappedComponentProps;

const DEFAULT_TIER_CODES = ['bronze', 'silver', 'gold', 'diamond'];
const LOYALTY_IMAGES_BY_CODE: { [key: string]: string } = {
    bronze: Bronze,
    silver: Silver,
    gold: Gold,
    diamond: Diamond,
};

const DEFAULT_LOYALTY_TIERS: LoyaltyTier[] = [
    {
        code: 'bronze',
        startAmount: 0,
        endAmount: 4999.99,
        percent: 3,
        multiplier: 1,
    },
    {
        code: 'silver',
        startAmount: 5000,
        endAmount: 9999.99,
        percent: 6,
        multiplier: 1,
    },
    {
        code: 'gold',
        startAmount: 10000,
        endAmount: 19999.99,
        percent: 10,
        multiplier: 1,
    },
    {
        code: 'diamond',
        startAmount: 20000,
        endAmount: null,
        percent: 12,
        multiplier: 1,
    },
];

const toNumber = (value: unknown, fallback: number) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeLoyaltyTiers = (tiers: any): LoyaltyTier[] => {
    if (!Array.isArray(tiers)) {
        return DEFAULT_LOYALTY_TIERS;
    }
    if (tiers.length === 0) {
        return [];
    }

    const normalized = tiers.map((tier: any, index: number) => {
        const fallbackTier = DEFAULT_LOYALTY_TIERS[index] || DEFAULT_LOYALTY_TIERS[DEFAULT_LOYALTY_TIERS.length - 1];
        const endAmountValue = tier?.endAmount !== undefined && tier?.endAmount !== null
            ? tier.endAmount
            : tier?.end_amount;

        return {
            code: tier?.code || fallbackTier.code,
            startAmount: toNumber(tier?.startAmount ?? tier?.start_amount, fallbackTier.startAmount),
            endAmount: endAmountValue === null || endAmountValue === undefined
                ? fallbackTier.endAmount
                : toNumber(endAmountValue, fallbackTier.endAmount === null ? Number.MAX_SAFE_INTEGER : fallbackTier.endAmount),
            percent: toNumber(tier?.percent, fallbackTier.percent),
            multiplier: toNumber(tier?.multiplier, fallbackTier.multiplier),
        };
    });

    return normalized.sort((a, b) => a.startAmount - b.startAmount);
};

const getTierCode = (code: string | undefined, index: number) => {
    const normalizedCode = code ? code.toLowerCase() : '';
    if (normalizedCode && LOYALTY_IMAGES_BY_CODE[normalizedCode]) {
        return normalizedCode;
    }
    return DEFAULT_TIER_CODES[index] || DEFAULT_TIER_CODES[DEFAULT_TIER_CODES.length - 1];
};

const getTierImage = (code: string | undefined, index: number) => LOYALTY_IMAGES_BY_CODE[getTierCode(code, index)] || Bronze;

const getTierLabel = (code: string | undefined, index: number) => {
    const tierCode = getTierCode(code, index);
    return tierCode.charAt(0).toUpperCase() + tierCode.slice(1);
};

const formatThresholdAmount = (amount: number) => Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");

const getDisplayedPercent = (tier: LoyaltyTier) => Math.round(tier.percent * tier.multiplier * 10) / 10;

const fetchCompanyLoyaltyRules = (dispatch: any, companyId: number): Promise<LoyaltyTier[]> => (
    new Promise((resolve, reject) => {
        dispatch(getCompanyLoyaltyRules(
            companyId,
            (responseJson: { loyaltyEnabled?: boolean; tiers?: LoyaltyTier[] }) => {
                if (responseJson?.loyaltyEnabled === false) {
                    resolve([]);
                    return;
                }
                resolve(normalizeLoyaltyTiers(responseJson?.tiers));
            },
            (error: any) => reject(error),
        ));
    })
);

class LoyaltyCard extends React.Component<LoyaltyProps> {
    /**
   * Get level informations
   * [currentLevelImage, nextLevelImage, nextLevelTotal]
   */
    getLevelInfos = (): LevelInfos => {
        const { loyalty, tiers } = this.props;
        const levels = normalizeLoyaltyTiers(tiers);
        const loyaltyCode = loyalty.loyalty ? loyalty.loyalty.toLowerCase() : '';

        let currentIndex = levels.findIndex((tier, index) => getTierCode(tier.code, index) === loyaltyCode);

        if (currentIndex < 0) {
            currentIndex = levels.findIndex((tier, index) => {
                const nextTier = levels[index + 1];
                const nextStartAmount = nextTier ? nextTier.startAmount : Number.POSITIVE_INFINITY;
                return loyalty.totalSpending >= tier.startAmount && loyalty.totalSpending < nextStartAmount;
            });
        }

        if (currentIndex < 0) {
            currentIndex = 0;
        }

        const currentTier = levels[currentIndex];
        const nextTier = levels[currentIndex + 1];

        return {
            currentImage: currentTier ? getTierImage(currentTier.code, currentIndex) : undefined,
            nextImage: nextTier ? getTierImage(nextTier.code, currentIndex + 1) : undefined,
            currentStartAmount: currentTier ? currentTier.startAmount : 0,
            nextStartAmount: nextTier ? nextTier.startAmount : undefined,
        };
    };

    getLevelCompletion = (levelInfos: LevelInfos) => {
        const { loyalty } = this.props;
        const { id, totalSpending } = loyalty;

        const bar = document.getElementById(`account-loyalty-card-${id}`);
        const completion = document.getElementById(`account-loyalty-card-completion-${id}`);

        if (bar && completion) {
            const total = bar.getBoundingClientRect().width;
            if (levelInfos.nextStartAmount !== undefined) {
                const levelRange = levelInfos.nextStartAmount - levelInfos.currentStartAmount;
                if (levelRange <= 0) {
                    return '0px';
                }
                const completionPercent = (totalSpending - levelInfos.currentStartAmount) / levelRange;
                const boundedCompletion = Math.min(Math.max(completionPercent, 0), 1);
                return `${boundedCompletion * total}px`;
            }
            return '0px';
        }

        return '0px';
    };

    render() {
        const { loyalty, school } = this.props;
        const level = this.getLevelInfos();
        return (
            <div className="__account-loyalty-card">
                <span className="__account-loyalty-card-header">
                    <p className="__account-loyalty-card-title">
                        {loyalty.loyalty && loyalty.loyalty.charAt(0).toUpperCase() + loyalty.loyalty.slice(1)}
                    </p>
                    <span className="flex-align">
                        <p>{`CHF ${loyalty.points?.toFixed(2)}`}</p>
                        {/* <LocalPlayIcon style={{ marginLeft: '5px', color: "#e4cb1c" }} /> */}
                    </span>
                </span>
                {level.currentImage && (
                    <img
                        className="__account-loyalty-card-image"
                        src={level.currentImage}
                        alt={school?.name}
                        title={school?.name}
                    />
                )}
                {school && <p className="__account-loyalty-card-school">{school.name}</p>}
                {level.nextImage && (
                    <div className="flex-align" style={{ justifyContent: 'flex-end' }}>
                        <div
                            id={`account-loyalty-card-${loyalty.id}`}
                            className="__account-loyalty-card-level-bar"
                        >
                            <div
                                id={`account-loyalty-card-completion-${loyalty.id}`}
                                className="__account-loyalty-card-level-bar-completion"
                                style={{ width: this.getLevelCompletion(level) }}
                            />
                        </div>
                        <img
                            className="__account-loyalty-card-level-image"
                            src={level.nextImage}
                            alt={school?.name}
                            title={school?.name}
                        />
                        <p style={{ whiteSpace: 'nowrap', fontSize: '18px' }}>
                            {level.nextStartAmount !== undefined
                                ? Math.max(level.nextStartAmount - loyalty.totalSpending, 0).toFixed(2)
                                : 0}
                        </p>
                    </div>
                )}
            </div>
        );
    }
}
const LoyaltyCardIntl = injectIntl(LoyaltyCard);

type ReduxProps = ConnectedProps<typeof connector>;

interface CustomerLoyaltyIProps {}

type CustomerLoyaltyProps = CustomerLoyaltyIProps & ReduxProps & WrappedComponentProps;

function CustomerLoyalty(props: CustomerLoyaltyProps) {
    const {
        intl,
        customerLoyalty,
        schools,
        currentSchool,
    } = props;
    const dispatch = useDispatch();
    const history = useHistory();
    const { lang } = useParams<{ lang: string | undefined }>();
    const [companyRules, setCompanyRules] = React.useState<{ [key: number]: LoyaltyTier[] }>({});

    const companyIds = React.useMemo(() => {
        const ids = new Set<number>();
        if (currentSchool?.id) {
            ids.add(currentSchool.id);
        }
        if (customerLoyalty && customerLoyalty.length > 0) {
            customerLoyalty.forEach((loyalty: any) => {
                const companyId = Number(loyalty?.companyId);
                if (Number.isFinite(companyId) && companyId > 0) {
                    ids.add(companyId);
                }
            });
        }
        return Array.from(ids);
    }, [customerLoyalty, currentSchool]);

    React.useEffect(() => {
        let isCancelled = false;
        const missingCompanyIds = companyIds.filter((companyId) => !companyRules[companyId]);

        if (missingCompanyIds.length === 0) {
            return () => {
                isCancelled = true;
            };
        }

        const loadCompanyRules = async () => {
            const fetchedRules = await Promise.all(
                missingCompanyIds.map(async (companyId) => {
                    try {
                        const rules = await fetchCompanyLoyaltyRules(dispatch, companyId);
                        return [companyId, rules] as [number, LoyaltyTier[]];
                    } catch (error) {
                        return null;
                    }
                }),
            );

            if (!isCancelled) {
                const successfulRules = fetchedRules.filter(
                    (result): result is [number, LoyaltyTier[]] => result !== null,
                );
                if (successfulRules.length > 0) {
                    setCompanyRules((currentRules) => {
                        const nextRules = { ...currentRules };
                        successfulRules.forEach(([companyId, rules]) => {
                            nextRules[companyId] = rules;
                        });
                        return nextRules;
                    });
                }
            }
        };

        loadCompanyRules();

        return () => {
            isCancelled = true;
        };
    }, [companyIds, companyRules, dispatch]);

    const selectedCompanyId = currentSchool?.id
        || (customerLoyalty && customerLoyalty[0] ? Number(customerLoyalty[0].companyId) : undefined);
    const selectedCompanyRules = normalizeLoyaltyTiers(
        selectedCompanyId ? companyRules[selectedCompanyId] : DEFAULT_LOYALTY_TIERS,
    );

    return (
        <div>
            <div>
                <Title className="__account-loyalty-title">
                    {intl.formatMessage({ id: 'account.customerLoyalty.title1' })}
                </Title>
                {customerLoyalty && customerLoyalty.length > 0 ? (
                    <div className="__account-loyalty-cards">
                        {customerLoyalty
                            && customerLoyalty.map((l: any) => {
                                const companyId = Number(l.companyId);
                                return (
                                    <LoyaltyCardIntl
                                        key={`account-loyalty-${l.companyId}`}
                                        loyalty={l}
                                        tiers={companyRules[companyId] || DEFAULT_LOYALTY_TIERS}
                                        school={schools ? schools.find((s) => s.id === l.companyId) : undefined}
                                    />
                                );
                            })}
                    </div>
                ) : (
                    <div className="__account-loyalty-no-points">
                        {intl.formatMessage({ id: 'account.customerLoyalty.noPoint' })}
                        {' '}
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,
                                jsx-a11y/no-noninteractive-element-interactions */}
                        <p
                            className="__account-loyalty-no-points-link"
                            onClick={() => history.push(`/${lang}/products`)}
                        >
                            {intl.formatMessage({ id: 'account.customerLoyalty.noPoint.link' })}
                        </p>
                    </div>
                )}
            </div>
            <br />
            <br />
            <div>
                <Title className="__account-loyalty-title">
                    {intl.formatMessage({ id: 'account.customerLoyalty.title2' })}
                </Title>
                <div>
                    {selectedCompanyRules.map((tier, index) => (
                        <div
                            className="__account-loyalty-section"
                            key={`account-loyalty-rule-${getTierCode(tier.code, index)}-${tier.startAmount}`}
                        >
                            <img
                                className="__account-loyalty-image"
                                src={getTierImage(tier.code, index)}
                                alt={getTierLabel(tier.code, index)}
                                title={getTierLabel(tier.code, index)}
                            />
                            <span>
                                <p>
                                    {intl.formatMessage(
                                        { id: 'account.customerLoyalty.bonus' },
                                        { percent: getDisplayedPercent(tier) },
                                    )}
                                </p>
                                {tier.startAmount > 0 ? (
                                    <p>
                                        {intl.formatMessage(
                                            { id: 'account.customerLoyalty.reach' },
                                            { amount: formatThresholdAmount(tier.startAmount) },
                                        )}
                                    </p>
                                ) : null}
                            </span>
                        </div>
                    ))}
                    <div className="__account-loyalty-explanations">
                        <p>{intl.formatMessage({ id: 'account.customerLoyalty.explanations1' })}</p>
                        <p>{intl.formatMessage({ id: 'account.customerLoyalty.explanations2' })}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = (store: ApplicationState) => ({
    customerLoyalty: store.setting.userData.customerLoyalty,
    schools: store.setting.schools,
    currentSchool: store.navigation.currentSchool,
});

const connector = connect(mapStateToProps);

export default injectIntl(connector(CustomerLoyalty));
